import { webConfig } from '@gitbook/env';
import { Logger, ReportException, Trace } from '@gitbook/util-debug';

import { type firebase, storage } from '../db';
import { getBucketNameFromLocation } from './buckets';
import { getFilePath } from './paths';
import type { FileLocation, FileUploadMetadata, NativeFile } from './types';
import { isImageMimeType } from './utils';

const logger = Logger('storage');

/**
 * Gets the dimensions for an image identified in path using a native web tag.
 */
async function getImageDimensionsWeb(
    imageSource: Blob | string
): Promise<{ width: number; height: number }> {
    return new Promise<{ width: number; height: number }>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({
                width: img.naturalWidth,
                height: img.naturalHeight,
            });
        };
        img.onerror = (error) => {
            reject(error);
        };

        img.src = typeof imageSource === 'string' ? imageSource : URL.createObjectURL(imageSource);
    });
}

/**
 * Upload a file and return the download url.
 */
export async function uploadNativeFile(
    location: FileLocation,
    file: NativeFile
): Promise<FileUploadMetadata> {
    if (getBucketNameFromLocation(location) !== webConfig.firebase.storageBucket) {
        throw new Error(`Cannot upload a ${location.kind} file from the web.`);
    }

    const path = getFilePath(location, file);
    const storageRef = storage().ref(path);

    logger.info(
        `upload file "${file.name}" at "${path}" (size is ${(file.size / 1024).toFixed(
            0
        )}KB, type is "${file.type}")`
    );

    // Upload file to Firebase
    // @ts-ignore firestore wants a Blob but it's actually the same as a NativeFile
    const blob: Blob = file;

    let dimensions: { width: number; height: number } | undefined;
    if (isImageMimeType(file.type)) {
        try {
            dimensions = await getImageDimensionsWeb(blob);
        } catch (e) {
            logger.warning(
                'Could not get image dimensions using an Image tag. File will be uploaded without dimensions set in the metadata.',
                e
            );
        }
    }

    const snapshot = await new Trace('storage.uploadNativeFile')
        .wrap(async () =>
            storageRef.put(blob, {
                customMetadata: dimensions
                    ? {
                          height: dimensions.height.toString(),
                          width: dimensions.width.toString(),
                      }
                    : {},
            })
        )
        .catch((error) => rethrowStorageError(error, 'upload', storageRef, location.kind));

    return getFileMetadata(snapshot.ref, location.kind, snapshot.metadata);
}

/**
 * Returns the metadata for a given path.
 */
export async function getNativeFileMetadata(
    bucketName: string,
    filePath: string,
    fileKind?: FileLocation['kind']
): Promise<FileUploadMetadata | null> {
    if (bucketName !== webConfig.firebase.storageBucket) {
        throw new Error(
            `Cannot retrieve file metadata for files in the ${bucketName} bucket from the web.`
        );
    }

    const storageRef = storage().ref(filePath);
    try {
        const metadata = await getFileMetadata(storageRef, fileKind);
        return metadata;
    } catch (error) {
        // https://firebase.google.com/docs/storage/web/handle-errors
        if (
            !(typeof error === 'object' && error && 'code' in error) ||
            error.code !== 'storage/object-not-found'
        ) {
            throw error;
        }

        return null;
    }
}

/**
 * Delete all files at a location
 */
export async function deleteAllFiles(): Promise<void> {
    throw new Error('not implemented for web');
}

/**
 * Shortcut method to duplicate a file in a GCS bucket to another location given its download url.
 */
export async function duplicateFileInBucket(): Promise<never> {
    throw new Error('not implemented for web');
}

/**
 * Prepare metadata from a storage file. Optionally accepts metadata as a parameter,
 * so we avoid an extra call when we upload.
 */
async function getFileMetadata(
    storageRef: firebase.storage.Reference,
    fileKind: FileLocation['kind'] | undefined,
    existingMetadata?: firebase.storage.FullMetadata
): Promise<FileUploadMetadata> {
    const [metadata, downloadURL] = await Promise.all([
        existingMetadata
            ? existingMetadata
            : new Trace('storage.getMetadata')
                  .wrap(async () => storageRef.getMetadata())
                  .catch((error) =>
                      rethrowStorageError(error, 'getMetadata', storageRef, fileKind)
                  ),
        new Trace('storage.getDownloadURL')
            .wrap(async () => storageRef.getDownloadURL())
            .catch((error) => rethrowStorageError(error, 'getDownloadURL', storageRef, fileKind)),
    ]);

    const dimensions: { width?: number; height?: number } = {};
    if (metadata.customMetadata) {
        const width = Number.parseInt(metadata.customMetadata.width, 10);
        if (width && !Number.isNaN(width)) {
            dimensions.width = width;
        }

        const height = Number.parseInt(metadata.customMetadata.height, 10);
        if (height && !Number.isNaN(height)) {
            dimensions.height = height;
        }
    }

    return {
        name: storageRef.name,
        size: metadata.size,
        contentType: metadata.contentType || '',
        ...(dimensions.width && dimensions.height
            ? ({ dimensions } as { dimensions: { width: number; height: number } })
            : {}),
        downloadURL,
    };
}

function rethrowStorageError(
    error: unknown,
    operation: 'upload' | 'getMetadata' | 'getDownloadURL',
    storageRef: firebase.storage.Reference,
    fileKind: FileLocation['kind'] | undefined
): never {
    if (
        error instanceof Error &&
        'code' in error &&
        error.code === 'storage/unauthorized' &&
        fileKind
    ) {
        ReportException.attachOptions(error, {
            fingerprint: [error.code, operation, fileKind],
            extra: { storageBucket: storageRef.bucket, storagePath: storageRef.fullPath },
        });
    }
    throw error;
}
